import getWindow from 'Utils/get-window';
import collectVitals from 'Utils/report-vitals';
import normalizedPathname from 'Utils/normalized-pathname';
import pageType from 'Utils/analytics/page-type';
import isCrawler from 'Utils/is-crawler';
import store from 'Webapp/shared/app/redux/store';
import { usageTrackWebVitals } from 'Webapp/shared/app/redux/actions/usage-actions';

/**
 * Fraction of page views that report Core Web Vitals.
 *
 * flipboard.com serves ~12M browser page views a day. Reporting every one would
 * add an event stream comparable to the largest existing web usage event, for a
 * measurement that only needs enough samples to hold a stable p75/p99. At 10%
 * that is ~1.2M/day, which is far more than the percentiles need and under 1%
 * added volume on `core_schema.usage_event_*`.
 *
 * Runtime on/off lives in the WEB_VITALS_REPORTING feature flag (dynconfig,
 * no deploy). Changing this rate is a deploy, same as the ad unit paths in
 * `ads-config.ts`.
 */
export const WEB_VITALS_SAMPLE_RATE = 0.1;

export default () => {
  const w = getWindow();
  const { navigator } = w;

  if (!navigator || !navigator.userAgent) {
    return;
  }
  // Crawlers render the page but are not users; their timings would swamp the
  // distribution. `sendUsageEventWithSession` drops crawler events server-side
  // too, this just avoids doing the work at all.
  if (isCrawler({ get: () => navigator.userAgent })) {
    return;
  }
  if (!store.getState().featureFlags.WEB_VITALS_REPORTING) {
    return;
  }
  if (Math.random() >= WEB_VITALS_SAMPLE_RATE) {
    return;
  }

  // Captured at load: by the time the metrics settle the user may have
  // client-navigated away, and the timings belong to the page they were
  // measured on.
  const href = w.location && w.location.href;
  const pathname = (w.location && w.location.pathname) || '';
  const url = normalizedPathname(href);
  const page_type = pageType(pathname);

  // May fire more than once for one page view: a reader who switches tabs and
  // comes back keeps accruing CLS and INP. Each report carries pv_id / pv_seq
  // so the last one per pv_id wins downstream. See Utils/report-vitals.
  collectVitals((metrics) => {
    // Fire and forget. This runs while the page is hiding, so nothing useful
    // can be done with the result and nothing should block the unload.
    store.dispatch(
      usageTrackWebVitals({
        ...metrics,
        url,
        page_type,
      }),
    );
  });
};
